import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { Database } from '@/integrations/supabase/types';
import { logUserLogin, logUserLogout } from '@/utils/action-logger';
import { sendAdminPendingConfirmationNotify } from '@/utils/email-service';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  isLoading: boolean;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string, captchaToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isApproved: boolean;
  isSuspended: boolean;
};

export type UserProfile = {
  id: string;
  role: 'customer' | 'administrator';
  is_approved: boolean;
  suspended: boolean;
  first_name: string | null;
  last_name: string | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const isAdmin = profile?.role === 'administrator';
  const isApproved = profile?.is_approved === true;
  const isSuspended = profile?.suspended === true;

  useEffect(() => {
    console.log('Setting up auth state listener...');
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('Auth state changed:', event);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (event === 'SIGNED_OUT') {
          setProfile(null);
        } else if (currentSession?.user && event !== 'INITIAL_SESSION') {
          // Skip profile fetch during initial session and signup
          // This prevents duplicate profile creation
          setTimeout(() => {
            fetchUserProfile(currentSession.user.id);
          }, 0);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      console.log('Got existing session:', currentSession ? 'yes' : 'no');
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (currentSession?.user) {
        fetchUserProfile(currentSession.user.id);
      } else {
        setIsLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchUserProfile = async (userId: string) => {
    try {
      // Use the RPC function to get the profile safely
      const { data, error } = await supabase
        .rpc('get_profile_by_id', { p_id: userId });
      
      if (error) {
        console.error('Profile fetch error:', error);
        throw error;
      }
      
      // Check if data exists and has at least one profile
      const profileData = data && Array.isArray(data) && data.length > 0 ? data[0] : null;
      
      if (!profileData) {
        console.error('No profile found for user:', userId);
        await handleUnapprovedUser();
        return;
      }
      
      setProfile(profileData as UserProfile);
      
      // Handle suspended users
      if (profileData.suspended) {
        console.log('User is suspended, redirecting to suspended page');
        if (window.location.pathname !== '/auth/suspended') {
          navigate('/auth/suspended', { replace: true });
        }
        // Return early to avoid the approval check
        setIsLoading(false);
        return;
      }
      
      if (!profileData.is_approved) {
        console.log('User not approved, redirecting to pending approval');
        await handleUnapprovedUser();
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Error fetching profile. Please try logging in again."
      });
      await signOutInternal();
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleUnapprovedUser = async () => {
    if (window.location.pathname !== '/auth/pending-approval') {
      navigate('/auth/pending-approval', { replace: true });
    }
  };

  const signOutInternal = async () => {
    try {
      // Log logout before clearing state if user exists
      if (user) {
        await logUserLogout(user.id);
      }

      // Clear local state first to avoid UI flashing
      setUser(null);
      setProfile(null);
      setSession(null);
      
      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error during sign out:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in internal sign out:', error);
    }
  };

  const tryNotifyAdminPendingConfirmation = (
    userId: string,
    email: string,
    firstName: string,
    lastName: string
  ) => {
    void sendAdminPendingConfirmationNotify({
      userId,
      email,
      firstName,
      lastName,
    });
  };

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      setIsLoading(true);
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });

      if (error) throw error;

      // Log successful login
      if (data.user) {
        await logUserLogin(data.user.id);
      }
      
      toast({
        title: "Success",
        description: 'Successfully signed in'
      });
    } catch (error: any) {
      console.error('Error signing in:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to sign in'
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string, captchaToken?: string) => {
    try {
      // 1. First register the user with auth
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          captchaToken,
          data: {
            first_name: firstName,
            last_name: lastName,
          },
          emailRedirectTo: `${window.location.origin}/auth/login`,
        },
      });

      if (error) {
        console.error('Auth signup error:', error);
        throw error;
      }

      if (!data.user) {
        throw new Error('No user returned from signup');
      }

      // 2. Try to get existing profile first
      const { data: existingProfile, error: fetchError } = await supabase
        .rpc('get_profile_by_id', { p_id: data.user.id });

      // Check if data exists and has at least one profile
      if (!fetchError && existingProfile && Array.isArray(existingProfile) && existingProfile.length > 0) {
        // Profile already exists, use it
        setProfile(existingProfile[0] as UserProfile);
        tryNotifyAdminPendingConfirmation(
          data.user.id,
          email,
          firstName,
          lastName
        );
        toast({
          title: "Success",
          description: "Registration successful! Please check your email to confirm your account."
        });
        navigate('/auth/pending-approval');
        return;
      }

      // 3. Create new profile if none exists
      const { data: profileData, error: profileError } = await supabase.rpc('create_new_profile', {
        p_user_id: data.user.id,
        p_first_name: firstName,
        p_last_name: lastName,
        p_role: 'customer',
        p_is_approved: false
      });

      if (profileError) {
        console.error('Profile creation error:', profileError);
        
        // If it's a duplicate key error, try to fetch the profile again
        if (profileError.code === '23505') {
          const { data: retryProfile, error: retryError } = await supabase
            .rpc('get_profile_by_id', { p_id: data.user.id });
            
          // Check if data exists and has at least one profile
          if (!retryError && retryProfile && Array.isArray(retryProfile) && retryProfile.length > 0) {
            setProfile(retryProfile[0] as UserProfile);
            tryNotifyAdminPendingConfirmation(
              data.user.id,
              email,
              firstName,
              lastName
            );
            toast({
              title: "Success",
              description: "Registration successful! Please check your email to confirm your account."
            });
            navigate('/auth/pending-approval');
            return;
          }
        }
        
        // If we get here, something went wrong
        await signOutInternal();
        throw profileError;
      }

      if (!profileData) {
        await signOutInternal();
        throw new Error('No profile data returned');
      }

      setProfile(profileData as UserProfile);
      tryNotifyAdminPendingConfirmation(
        data.user.id,
        email,
        firstName,
        lastName
      );
      toast({
        title: "Success",
        description: "Registration successful! Please check your email to confirm your account."
      });
      navigate('/auth/pending-approval');
    } catch (error: any) {
      console.error('Error in signup process:', error.message);
      
      // Show error toast here instead of in the Register component
      toast({
        variant: "destructive",
        title: "Registration Failed",
        description: "Failed to create account. Please try again."
      });
      
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      
      await signOutInternal();
      
      // Only show success message and redirect if no error
      toast({
        title: "Success", 
        description: 'Successfully signed out'
      });
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('Error signing out:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to sign out'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        isLoading,
        signIn,
        signUp,
        signOut,
        isAdmin,
        isApproved,
        isSuspended,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
